import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [unreadSurveysCount, setUnreadSurveysCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadSurveysCount = useCallback(async () => {
    if (!token || !user) {
      setUnreadSurveysCount(0);
      return;
    }

    try {
      const response = await axios.get(`${API}/surveys/unread/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUnreadSurveysCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread surveys count:', error);
      setUnreadSurveysCount(0);
    }
  }, [token, user]);

  const fetchUnreadNotificationsCount = useCallback(async () => {
    if (!token || !user) {
      setUnreadNotificationsCount(0);
      return;
    }

    try {
      const response = await axios.get(`${API}/notifications/unread/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUnreadNotificationsCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread notifications count:', error);
      setUnreadNotificationsCount(0);
    }
  }, [token, user]);

  const fetchAll = useCallback(async () => {
    if (!token || !user) return;
    setLoading(true);
    await Promise.all([
      fetchUnreadSurveysCount(),
      fetchUnreadNotificationsCount()
    ]);
    setLoading(false);
  }, [token, user, fetchUnreadSurveysCount, fetchUnreadNotificationsCount]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (token && user) {
      fetchAll();
      
      // Refresh every 2 minutes
      const interval = setInterval(fetchAll, 120000);
      return () => clearInterval(interval);
    }
  }, [token, user, fetchAll]);

  // Function to manually refresh
  const refreshNotifications = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  // Function to decrement when user responds to a survey
  const markSurveyAsRead = useCallback(() => {
    setUnreadSurveysCount(prev => Math.max(0, prev - 1));
  }, []);

  // Function to decrement notifications count
  const markNotificationAsRead = useCallback((count = 1) => {
    setUnreadNotificationsCount(prev => Math.max(0, prev - count));
  }, []);

  return (
    <NotificationContext.Provider 
      value={{ 
        unreadSurveysCount, 
        unreadNotificationsCount,
        loading,
        refreshNotifications,
        markSurveyAsRead,
        markNotificationAsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
